import { motion } from "framer-motion";
import { Fragment, useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { fmtDur } from "@/lib/format";
import { Announcement } from "@/components/home/Announcement";
import { ChecklistModal } from "@/components/home/ChecklistModal";
import { CHECKLIST_QUESTIONS, COUNTRIES, fade, PROGRAMS } from "@/components/home/data";
import { FeedbackSection } from "@/components/home/FeedbackSection";
import { FestivalCorner } from "@/components/home/FestivalCorner";
import { NavTabs } from "@/components/home/NavTabs";
import { ProgramsSection } from "@/components/home/ProgramsSection";
import { RegisterModal } from "@/components/home/RegisterModal";
import { RegisterChoiceDialog, SchoolFormDialog, useSchoolsEnabled } from "@/components/home/SchoolRegister";
import { SiteModals } from "@/components/home/SiteModals";
import { SiteTitle, siteTitleInline } from "@/components/home/SiteTitle";
import { TestimonialsSection } from "@/components/home/TestimonialsSection";
import { useLayout } from "@/hooks/useLayout";
import { LayoutPreviewBanner } from "@/components/LayoutPreviewBanner";

const checkDuration = (file, kind, maxSec) =>
  new Promise((resolve) => {
    const el = document.createElement(kind === "audio" ? "audio" : "video");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      const d = el.duration;
      URL.revokeObjectURL(el.src);
      resolve(Number.isFinite(d) ? d <= maxSec + 0.5 : true);
    };
    el.onerror = () => resolve(true); // can't read metadata → rely on backend size cap
    el.src = URL.createObjectURL(file);
  });

export default function Home() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", profession: "", place: "", program_name: PROGRAMS[0].name });
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState(null); // 'about' | 'contact' | 'registered' | null
  const [regMessage, setRegMessage] = useState("");
  const [regOpen, setRegOpen] = useState(true);
  const [formUnlocked, setFormUnlocked] = useState(false);
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checks, setChecks] = useState(CHECKLIST_QUESTIONS.map(() => false));
  const [pendingProgram, setPendingProgram] = useState("");
  const [agreedAt, setAgreedAt] = useState("");
  const [regCount, setRegCount] = useState(0);
  const [regLimit, setRegLimit] = useState(0);
  const [content, setContent] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [fbForm, setFbForm] = useState({ name: "", message: "" });
  const [fbKind, setFbKind] = useState("text");
  const [fbFile, setFbFile] = useState(null);
  const [fbBusy, setFbBusy] = useState(false);
  const [fbDone, setFbDone] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [guides, setGuides] = useState([]);
  const [dialCode, setDialCode] = useState("+91");
  const [mobileLocal, setMobileLocal] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const lay = useLayout();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 320);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const refreshCount = useCallback(() => {
    api.get("/registrations/count").then(({ data }) => {
      setRegOpen(data.open);
      setRegCount(data.count);
      setRegLimit(data.limit);
    }).catch((err) => console.warn("Could not load registration count", err));
  }, []);

  useEffect(() => {
    refreshCount();
    api.get("/content").then(({ data }) => {
      setContent(data);
      const firstEnabled = (data.programs || []).find((p) => p.register_enabled) || (data.programs || [])[0];
      if (firstEnabled?.name) setForm((f) => ({ ...f, program_name: firstEnabled.name }));
    }).catch(() => {});
    api.get("/feedback").then(({ data }) => setFeedbacks(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/guides").then(({ data }) => setGuides(data)).catch(() => {});
  }, [refreshCount]);

  const uploadMedia = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data; // { url, kind, content_type }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (fbKind === "text" && !fbForm.message.trim()) { toast.error("Please write your feedback"); return; }
    if (fbKind !== "text" && !fbFile) { toast.error(`Please choose an ${fbKind} file`); return; }
    if (fbKind !== "text") {
      const maxSec = fbKind === "audio" ? (content?.audio_max_sec || 120) : (content?.video_max_sec || 60);
      const ok = await checkDuration(fbFile, fbKind, maxSec);
      if (!ok) { toast.error(`${fbKind === "audio" ? "Audio" : "Video"} must be ${fmtDur(maxSec)} or shorter.`); return; }
    }
    setFbBusy(true);
    try {
      let payload = { name: fbForm.name, kind: fbKind };
      if (fbKind === "text") {
        payload.message = fbForm.message;
      } else {
        const m = await uploadMedia(fbFile);
        payload = { ...payload, media_url: m.url, content_type: m.content_type };
      }
      const { data } = await api.post("/feedback", payload);
      setFbDone(data.message);
      setFbForm({ name: "", message: "" });
      setFbFile(null);
      setFbKind("text");
      api.get("/feedback").then(({ data }) => setFeedbacks(data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setFbBusy(false);
    }
  };

  const checklistQuestions = (content?.checklist_questions?.length ? content.checklist_questions : CHECKLIST_QUESTIONS);

  const schoolsEnabled = useSchoolsEnabled();
  const [choice, setChoice] = useState(null); // { name } while the Individual/School popup is open
  const [schoolOpen, setSchoolOpen] = useState(false);
  const registerFor = (name, skipChoice = false) => {
    if (!regOpen) return;
    if (schoolsEnabled && !skipChoice) { setChoice({ name }); return; }
    setPendingProgram(name);
    setForm((f) => ({ ...f, program_name: name }));
    if (formUnlocked || content?.checklist_enabled === false) {
      setFormUnlocked(true);
      setRegModalOpen(true);
      return;
    }
    setChecks(checklistQuestions.map(() => false));
    setChecklistOpen(true);
  };

  const completeChecklist = () => {
    if (!checks.every(Boolean)) return;
    setAgreedAt(new Date().toISOString());
    setFormUnlocked(true);
    setChecklistOpen(false);
    setForm((f) => ({ ...f, program_name: pendingProgram || f.program_name }));
    setRegModalOpen(true);
  };

  const submit = async (e, repeatFlags = null) => {
    if (e?.preventDefault) e.preventDefault();
    const digits = mobileLocal.replace(/\D/g, "");
    const country = COUNTRIES.find((c) => c.code === dialCode);
    if (dialCode !== "+91" && /^[6-9]\d{9}$/.test(mobileLocal.replace(/\D/g, "")) && !window.confirm(`You chose ${dialCode} (${country?.name}). This looks like an Indian mobile number — should it be +91 India? Press Cancel to go back and change the country code, or OK to continue with ${dialCode}.`)) return;
    const min = country?.len ?? country?.min ?? 6;
    const max = country?.len ?? country?.max ?? 15;
    if (!digits) {
      toast.error("Please enter your mobile number");
      return null;
    }
    if (digits.length < min || digits.length > max) {
      const range = min === max ? `${min} digits` : `${min}–${max} digits`;
      toast.error(`Enter a valid mobile number (${country?.name || "selected country"} needs ${range}).`);
      return null;
    }
    const emailValue = form.email.trim();
    if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue)) {
      toast.error("Please enter a valid email address (e.g. name@gmail.com).");
      return null;
    }
    setSending(true);
    try {
      const waRaw = (form.whatsapp || "").replace(/[^\d+]/g, "");
      const whatsapp = waRaw ? (waRaw.startsWith("+") ? waRaw : `${dialCode}${waRaw}`) : `${dialCode}${digits}`;
      const formFields = { ...form };
      delete formFields.whatsapp_same;
      delete formFields.whatsapp;
      const payload = { ...formFields, whatsapp, email: emailValue || null, mobile: `${dialCode}${digits}`, batch_no: "", checklist_agreed: true, checklist_agreed_at: agreedAt || new Date().toISOString(), checklist_items: checklistQuestions, ...(repeatFlags || {}) };
      const { data } = await api.post("/register", payload);
      if (data?.repeat_prompt || data?.repeat_blocked) return data;
      setRegMessage(data.message);
      setModal(data?.pending ? "pending" : "registered");
      setForm({ name: "", mobile: "", email: "", profession: "", place: "", program_name: PROGRAMS[0].name });
      setMobileLocal("");
      setRegModalOpen(false);
      refreshCount();
      return data;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        setRegMessage(detail || "You have already registered");
        setModal("duplicate");
        setRegModalOpen(false);
        refreshCount();
        return null;
      }
      toast.error(detail || "Something went wrong. Please try again.");
      refreshCount();
      return null;
    } finally {
      setSending(false);
    }
  };

  const pageBlocks = {
    announcement: content?.announcement_visible !== false && <Announcement content={content} />,
    programs: <ProgramsSection content={content} regLimit={regLimit} regCount={regCount} onRegister={registerFor} layout={lay.layout.home} layoutReady={lay.ready} />,
    feedback: content?.feedback_visible && <FeedbackSection content={content} fbDone={fbDone} setFbDone={setFbDone} onSubmit={submitFeedback} fbKind={fbKind} setFbKind={setFbKind} fbFile={fbFile} setFbFile={setFbFile} fbForm={fbForm} setFbForm={setFbForm} fbBusy={fbBusy} feedbacks={feedbacks} />,
    testimonials: content?.testimonials_visible && testimonials.length > 0 && <TestimonialsSection testimonials={testimonials} />,
  };

  return (
    <main data-testid="home-page" className="relative min-h-screen overflow-hidden">
      <Toaster position="top-center" richColors />
      {lay.preview && <LayoutPreviewBanner />}

      <div
        data-testid="sticky-header"
        className={`fixed top-0 left-0 right-0 z-50 border-b border-white/50 bg-[#F4F1EA]/85 backdrop-blur-md transition-all duration-300 ${scrolled ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"}`}
      >
        <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between gap-4">
          <span className="font-serif text-lg sm:text-xl tracking-tight whitespace-nowrap" data-testid="sticky-site-title">{siteTitleInline(content)}</span>
          <nav className="flex flex-wrap items-center justify-end gap-2">
            <NavTabs prefix="sticky-tab" content={content} guides={guides} testimonials={testimonials} onAbout={() => setModal("about")} onContact={() => setModal("contact")} />
          </nav>
        </div>
      </div>

      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F4F1EA] via-[#EDECE3] to-[#E4EAE0]" />
      <div className="absolute -z-10 top-[-10%] right-[-10%] w-[55vw] h-[55vw] rounded-full bg-[#A3B19B]/30 blur-[120px]" />
      <div className="absolute -z-10 bottom-[-15%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-[#C86A51]/15 blur-[120px]" />

      {content?.festival_visible !== false && content?.festival_image && (
        <FestivalCorner content={content} />
      )}

      <div className="mx-auto max-w-6xl px-6">
        <header className="pt-10">
          <div className="flex flex-col gap-4">
            <SiteTitle content={content} />
            <div data-testid="gratitude-line" className="flex items-center gap-4">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
              <span className="font-serif italic text-base md:text-lg text-accent whitespace-nowrap">{content?.gratitude_line || "With deep gratitude to all my teachers"}</span>
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
            </div>
            <nav className="flex flex-wrap items-center justify-end gap-2">
              <NavTabs prefix="tab" content={content} guides={guides} testimonials={testimonials} onAbout={() => setModal("about")} onContact={() => setModal("contact")} />
            </nav>
          </div>

          <motion.div
            data-testid="hero-deity-image"
            variants={fade} initial="hidden" animate="show"
            className="mt-8 flex justify-center"
          >
            <img
              src={content?.hero_image || "https://static.prod-images.emergentagent.com/jobs/710c1fa2-7af0-4464-9aa6-ae3ef7a8bf56/images/f1d42ac7fc4ad358ee8bbfe180c13d851f020608ccf433863706abe3ddc41a6a.jpeg"}
              alt="Program"
              className="w-44 h-44 sm:w-52 sm:h-52 rounded-3xl object-cover shadow-[0_12px_50px_rgba(200,106,81,0.25)] ring-4 ring-white/60"
            />
          </motion.div>
        </header>

        {lay.layout.home.page.filter((b) => b.visible).map((b) => <Fragment key={b.id}>{pageBlocks[b.id]}</Fragment>)}
        <RegisterChoiceDialog open={!!choice} onClose={() => setChoice(null)} onIndividual={() => { const n = choice.name; setChoice(null); registerFor(n, true); }} onSchool={() => { setPendingProgram(choice.name); setChoice(null); setSchoolOpen(true); }} />
        <SchoolFormDialog open={schoolOpen} onClose={() => setSchoolOpen(false)} onSubmitted={() => { setSchoolOpen(false); registerFor(pendingProgram || "PositiveLife", true); }} />
        <RegisterModal
          open={regModalOpen}
          onClose={() => setRegModalOpen(false)}
          onSubmit={submit}
          form={form}
          setForm={setForm}
          content={content}
          dialCode={dialCode}
          setDialCode={setDialCode}
          mobileLocal={mobileLocal}
          setMobileLocal={setMobileLocal}
          sending={sending}
          regOpen={regOpen}
        />

        <footer data-testid="site-footer" className="pb-12 pt-8 border-t border-border/60 flex flex-col md:flex-row md:items-center justify-between gap-5 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} {siteTitleInline(content)}</span>
          <div data-testid="contact-details" className="flex items-center gap-2">
            <MessageCircle size={16} className="text-accent" />
            <span>
              Contact:&nbsp;
              <a href="https://wa.me/919000571911" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-accent transition-colors">9000571911</a>
              &nbsp;/&nbsp;
              <a href="https://wa.me/919000571912" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-accent transition-colors">9000571912</a>
              &nbsp;(WhatsApp only)
            </span>
          </div>
          <span>{content?.footer_tagline || "Thought · Relationships · Health · Wealth"}</span>
        </footer>
      </div>

      <SiteModals modal={modal} onClose={() => setModal(null)} content={content} regMessage={regMessage} />
      <ChecklistModal
        open={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        content={content}
        questions={checklistQuestions}
        checks={checks}
        setChecks={setChecks}
        onComplete={completeChecklist}
      />
    </main>
  );
}
